import { configureStore, ThunkAction, Action, combineReducers } from '@reduxjs/toolkit';
import { persistStore, persistReducer, createTransform, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import authReducer from '../features/auth/authSlice';
import snackbarReducer from '../features/snackbar/snackbarSlice';
import projectReducer, { initialState as initialProjectState } from '../features/project/projectSlice';
import portalReducer from '../features/portal/portalSlice';

// Browsers that already persisted a null project (or one without a portal) would otherwise crash
// on rehydrate before the project query can replace it
const repairProjectState = createTransform(
  (inbound: any) => inbound,
  (outbound: any) => ({
    projects: Array.isArray(outbound?.projects) ? outbound.projects : [],
    project: outbound?.project?.portal ? outbound.project : initialProjectState.project,
  }),
  { whitelist: ['project'] }
);

const persistConfig = {
  key: 'root',
  version: 1,
  storage,
  transforms: [repairProjectState],
};

let rootReducers = combineReducers({
  auth: authReducer,
  snackbar: snackbarReducer,
  project: projectReducer,
  portal: portalReducer,
});

export type RootReducer = ReturnType<typeof rootReducers>;

const rootReducer = (state: any, action: any) => {
  if (action.type === 'auth/logout') {
    // for all keys defined in your persistConfig(s)
    storage.removeItem('persist:root');
    // storage.removeItem('persist:otherKey')

    return rootReducers(undefined, action);
  }
  return rootReducers(state, action);
};

const persistedReducer = persistReducer<RootReducer>(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

export const persistor = persistStore(store)

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>;
